import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { notificationsAPI, pushAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Bell, BellOff, Check, CheckCheck, Briefcase, Calendar,
  MessageCircle, Heart, Users, Star, Home, Loader2, AlertCircle, Trash2
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';

const NOTIFICATION_ICONS = {
  job: Briefcase,
  event: Calendar,
  message: MessageCircle,
  match: Heart,
  mentor: Users,
  review: Star,
  housing: Home,
  default: Bell,
};

const FILTER_TABS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'message', label: 'Messages' },
  { value: 'job', label: 'Jobs' },
  { value: 'event', label: 'Events' },
];

const Notifications = () => {
  usePageTitle('Notifications');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    fetchNotifications();
    checkPushSupport();
    // Auto-mark all as read when viewing the notifications page
    const autoMarkRead = async () => {
      try { await notificationsAPI.markAllRead(); } catch {}
    };
    const timer = setTimeout(autoMarkRead, 2000); // Mark read after 2s of viewing
    return () => clearTimeout(timer);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await notificationsAPI.list({});
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const checkPushSupport = () => {
    try {
      const supported = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
      setPushSupported(supported);
      setPushEnabled(supported && window.Notification && window.Notification.permission === 'granted');
    } catch (e) {
      setPushSupported(false);
      setPushEnabled(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handlePushToggle = async (enabled) => {
    try {
      if (enabled) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Push notification permission denied');
          return;
        }
        const vapidRes = await pushAPI.getVapidKey();
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidRes.data.public_key
        });
        await pushAPI.subscribe(subscription.toJSON());
        setPushEnabled(true);
        toast.success('Push notifications enabled!');
      } else {
        await pushAPI.unsubscribe();
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      }
    } catch (error) {
      toast.error('Failed to update push settings');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    return n.type === filter;
  });

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Notifications</h1>
            {unreadCount > 0 && (
              <p className="text-slate-gray mt-1">{unreadCount} unread</p>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} className="rounded-full">
              <CheckCheck className="w-4 h-4 mr-2" />Mark All Read
            </Button>
          )}
        </div>

        {/* Push notification banner */}
        {pushSupported && (
          <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${pushEnabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                {pushEnabled ? <Bell className="w-5 h-5 text-emerald-600" /> : <BellOff className="w-5 h-5 text-slate-gray" />}
              </div>
              <div>
                <p className="font-medium text-dark-slate text-sm">Push Notifications</p>
                <p className="text-xs text-slate-gray">{pushEnabled ? 'Enabled' : 'Get notified about new activity'}</p>
              </div>
            </div>
            <Switch checked={pushEnabled} onCheckedChange={handlePushToggle} />
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4">
          {FILTER_TABS.map(tab => (
            <button key={tab.value} onClick={() => setFilter(tab.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === tab.value ? 'bg-dark-slate text-white' : 'bg-white text-slate-gray border border-slate-200 hover:bg-slate-50'
              }`}>
              {tab.label}
              {tab.value === 'unread' && unreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Notification list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-gray" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
            <Bell className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">
              {filter === 'unread' ? "You're all caught up!" : 'No notifications'}
            </h3>
            <p className="text-slate-gray">
              {filter === 'unread' ? 'No unread notifications' : 'Nothing here yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map(notif => {
              const Icon = NOTIFICATION_ICONS[notif.type] || NOTIFICATION_ICONS.default;
              return (
                <div
                  key={notif.notification_id}
                  className={`bg-white rounded-2xl p-4 border shadow-sm transition-colors ${
                    notif.read ? 'border-slate-100' : 'border-l-4 border-l-dark-slate border-slate-100 bg-mint-fresh/5'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      notif.read ? 'bg-slate-100' : 'bg-dark-slate'
                    }`}>
                      <Icon className={`w-5 h-5 ${notif.read ? 'text-slate-gray' : 'text-white'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${notif.read ? 'text-slate-gray' : 'text-dark-slate font-medium'}`}>
                        {notif.title || notif.message}
                      </p>
                      {notif.title && notif.message && notif.title !== notif.message && (
                        <p className="text-xs text-slate-gray mt-0.5">{notif.message}</p>
                      )}
                      <p className="text-[10px] text-slate-gray mt-1">
                        {notif.created_at ? formatDistanceToNow(new Date(notif.created_at), { addSuffix: true }) : ''}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      {notif.link && (
                        <Link to={notif.link}>
                          <Button variant="ghost" size="sm" className="text-xs rounded-full h-7 px-3">View</Button>
                        </Link>
                      )}
                      {!notif.read && (
                        <button onClick={() => handleMarkRead(notif.notification_id)}
                          className="p-1.5 rounded-full hover:bg-slate-100" title="Mark as read">
                          <Check className="w-3.5 h-3.5 text-slate-gray" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Notifications;
